import {
  ordersRemainingFromRow,
  type LogisticPivotMetricColumnId,
  type LogisticPivotRow,
} from '../../models/LogisticPivot';

interface PivotMetricValueProps {
  metricId: LogisticPivotMetricColumnId;
  row: LogisticPivotRow;
  locale: string;
  /** Clase para el porcentaje entre paréntesis (p. ej. cards más compactas). */
  mutedClassName?: string;
}

/**
 * Conteos de estado: entregados, devueltos y resto como `N (xx,xx%)` sobre enviados de la fila.
 * Enviados: solo total.
 */
export function PivotMetricValue({
  metricId,
  row,
  locale,
  mutedClassName = 'text-muted-foreground',
}: PivotMetricValueProps) {
  const sent = row.orders_sent ?? 0;
  const del = row.orders_delivered ?? 0;
  const ret = row.orders_returned ?? 0;
  const rest = ordersRemainingFromRow(row);

  const shareOfSent = (numerator: number) =>
    sent > 0 ? (numerator / sent) * 100 : 0;

  const fmtInt = (n: number) => n.toLocaleString(locale);
  const fmtPct2 = (n: number) =>
    n.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (metricId === 'orders_sent') {
    return <span className="tabular-nums">{fmtInt(sent)}</span>;
  }

  if (metricId === 'orders_delivered') {
    return (
      <span className="tabular-nums">
        {fmtInt(del)}{' '}
        <span className={mutedClassName}>({fmtPct2(shareOfSent(del))}%)</span>
      </span>
    );
  }

  if (metricId === 'orders_returned') {
    return (
      <span className="tabular-nums">
        {fmtInt(ret)}{' '}
        <span className={mutedClassName}>({fmtPct2(shareOfSent(ret))}%)</span>
      </span>
    );
  }

  if (metricId === 'orders_remaining') {
    return (
      <span className="tabular-nums">
        {fmtInt(rest)}{' '}
        <span className={mutedClassName}>({fmtPct2(shareOfSent(rest))}%)</span>
      </span>
    );
  }

  return null;
}
